// Transport bar: tempo (+ tap), key/scale, bars, metronome/quantize toggles,
// play/record/clear, a beat indicator, and a record-state readout. Pure view.

import type { LooperState } from "../audio/looper.ts";
import { KEY_NAMES, SCALE_OPTIONS } from "../util/scales.ts";
import type { ScaleName } from "../util/types.ts";
import { iconButton, setIcon } from "./icons.ts";
import { createSwitch } from "./controls.ts";

export interface TransportUIHandlers {
  onPlayToggle(): void;
  onRecord(): void;
  onClear(): void;
  onBpmChange(bpm: number): void;
  onBarsChange(bars: number): void;
  onMetronomeToggle(on: boolean): void;
  onQuantizeToggle(on: boolean): void;
  onTapTempo(): void;
  onKeyChange(key: string): void;
  onScaleChange(scale: ScaleName): void;
}

export interface TransportUIInit {
  bpm: number;
  bars: number;
  beatsPerBar: number;
  metronome: boolean;
  quantize: boolean;
  key: string;
  scale: ScaleName;
}

export interface TransportUI {
  element: HTMLElement;
  setBpm(bpm: number): void;
  setBars(bars: number): void;
  setBarsEnabled(on: boolean): void;
  setKeyScale(key: string, scale: ScaleName): void;
  setMetronome(on: boolean): void;
  setQuantize(on: boolean): void;
  setPlaying(on: boolean): void;
  pulseBeat(beat: number): void;
  setRecordState(state: LooperState): void;
  setCountIn(beatsRemaining: number): void;
}

const BAR_OPTIONS = [1, 2, 4, 8] as const;

export function createTransportUI(
  handlers: TransportUIHandlers,
  init: TransportUIInit,
): TransportUI {
  const el = document.createElement("div");
  el.className = "transport";

  // Tempo
  const tempo = group("Tempo");
  const tempoVal = document.createElement("span");
  tempoVal.className = "tempo__val";
  tempoVal.textContent = `${init.bpm}`;
  tempo.label.append(tempoVal);
  const tempoRow = document.createElement("div");
  tempoRow.className = "tempo__row";
  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = "50";
  slider.max = "200";
  slider.step = "1";
  slider.value = String(init.bpm);
  slider.className = "slider";
  slider.setAttribute("aria-label", "Tempo in BPM");
  slider.addEventListener("input", () => handlers.onBpmChange(Number(slider.value)));
  const tapBtn = button("Tap", "btn btn--ghost btn--sm", () => handlers.onTapTempo());
  tempoRow.append(slider, tapBtn);
  tempo.body.append(tempoRow);

  // Key + scale
  const keyGroup = group("Key");
  const keySel = document.createElement("select");
  keySel.className = "select";
  for (const k of KEY_NAMES) keySel.append(option(k, k));
  keySel.value = init.key;
  keySel.addEventListener("change", () => handlers.onKeyChange(keySel.value));
  const scaleSel = document.createElement("select");
  scaleSel.className = "select";
  for (const s of SCALE_OPTIONS) scaleSel.append(option(s.value, s.label));
  scaleSel.value = init.scale;
  scaleSel.addEventListener("change", () => handlers.onScaleChange(scaleSel.value as ScaleName));
  const keyRow = document.createElement("div");
  keyRow.className = "key-row";
  keyRow.append(keySel, scaleSel);
  keyGroup.body.append(keyRow);

  // Bars
  const barsGroup = group("Bars");
  const seg = document.createElement("div");
  seg.className = "seg";
  const barButtons = new Map<number, HTMLButtonElement>();
  for (const n of BAR_OPTIONS) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "seg__btn";
    b.textContent = String(n);
    b.setAttribute("aria-pressed", String(n === init.bars));
    if (n === init.bars) b.classList.add("is-active");
    b.addEventListener("click", () => handlers.onBarsChange(n));
    barButtons.set(n, b);
    seg.append(b);
  }
  barsGroup.body.append(seg);

  // Feel toggles
  const feelGroup = group("Feel");
  const togRow = document.createElement("div");
  togRow.className = "tog-row";
  const metroBtn = createSwitch("Metronome", init.metronome, handlers.onMetronomeToggle);
  const quantBtn = createSwitch("Quantize", init.quantize, handlers.onQuantizeToggle);
  togRow.append(metroBtn.el, quantBtn.el);
  feelGroup.body.append(togRow);

  // Buttons + state (icon buttons with tooltips)
  const actions = document.createElement("div");
  actions.className = "transport__actions";
  const playBtn = iconButton("pause", "Play / pause (Space)", "iconbtn");
  playBtn.addEventListener("click", () => handlers.onPlayToggle());
  const recBtn = iconButton("record", "Record a layer (R)", "iconbtn iconbtn--rec");
  recBtn.addEventListener("click", () => handlers.onRecord());
  const clearBtn = iconButton("clear", "Clear all layers", "iconbtn iconbtn--ghost");
  clearBtn.addEventListener("click", () => handlers.onClear());
  actions.append(playBtn, recBtn, clearBtn);

  // Beat indicator + status
  const meta = document.createElement("div");
  meta.className = "transport__meta";
  const beats = document.createElement("div");
  beats.className = "beat-dots";
  const beatDots: HTMLSpanElement[] = [];
  for (let i = 0; i < init.beatsPerBar; i++) {
    const dot = document.createElement("span");
    dot.className = i === 0 ? "beat-dot beat-dot--down" : "beat-dot";
    beats.append(dot);
    beatDots.push(dot);
  }
  const status = document.createElement("span");
  status.className = "status-line";
  status.textContent = "Looping";
  meta.append(beats, status);

  el.append(tempo.group, keyGroup.group, barsGroup.group, feelGroup.group, actions, meta);

  let currentBeat = -1;

  return {
    element: el,
    setBpm(bpm) {
      slider.value = String(bpm);
      tempoVal.textContent = `${bpm}`;
    },
    setBars(bars) {
      for (const [n, b] of barButtons) {
        const active = n === bars;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", String(active));
      }
    },
    setBarsEnabled(on) {
      for (const b of barButtons.values()) b.disabled = !on;
      seg.classList.toggle("is-disabled", !on);
    },
    setKeyScale(key, scale) {
      keySel.value = key;
      scaleSel.value = scale;
    },
    setMetronome(on) {
      metroBtn.set(on);
    },
    setQuantize(on) {
      quantBtn.set(on);
    },
    setPlaying(on) {
      setIcon(playBtn, on ? "pause" : "play");
      playBtn.title = on ? "Pause (Space)" : "Play (Space)";
    },
    pulseBeat(beat) {
      if (currentBeat >= 0) beatDots[currentBeat]?.classList.remove("is-current");
      currentBeat = beat;
      beatDots[beat]?.classList.add("is-current");
    },
    setRecordState(state) {
      el.dataset["rec"] = state;
      recBtn.classList.toggle("is-armed", state !== "idle");
      if (state === "recording") status.textContent = "Recording";
      else if (state === "counting") status.textContent = "Armed";
      else status.textContent = "Looping";
    },
    setCountIn(beatsRemaining) {
      status.textContent = beatsRemaining > 0 ? `Count-in ${beatsRemaining}` : "Go";
    },
  };
}

function group(labelText: string): {
  group: HTMLDivElement;
  label: HTMLDivElement;
  body: HTMLDivElement;
} {
  const g = document.createElement("div");
  g.className = "ctl";
  const label = document.createElement("div");
  label.className = "ctl__label";
  const name = document.createElement("span");
  name.textContent = labelText;
  label.append(name);
  const body = document.createElement("div");
  body.className = "ctl__body";
  g.append(label, body);
  return { group: g, label, body };
}

function button(text: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.textContent = text;
  b.addEventListener("click", onClick);
  return b;
}

function option(value: string, label: string): HTMLOptionElement {
  const o = document.createElement("option");
  o.value = value;
  o.textContent = label;
  return o;
}

